import { Button } from "@faire/web--source/slate/Button";
import { Column } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import brandPortalIntegrations from "@faire/web-api--source/routes/www/brand-portal/integrations";
import brandPortalOrders from "@faire/web-api--source/routes/www/brand-portal/orders";
import React, { JSX } from "react";

import { Bold } from "../common/Bold";
import { LiItem } from "../common/LiItem";

export const OrderSyncWithShopifyIA = () => {
  return (
    <Column>
      <Typography>
        <StrictLocalMsg
          defaultMessage="If you’re experiencing issues with the order sync feature of your Shopify integration, follow these steps to troubleshoot:"
          description={{
            text: "Introduction to troubleshooting order sync with Shopify",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <ol>
        <LiItem step={1}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Go to your <LinkFormatting>Orders</LinkFormatting> page on Faire. Confirm you’ve accepted the order that hasn’t synced by selecting an expected ship date. Orders that aren’t accepted on Faire will not sync to Shopify."
              values={{
                LinkFormatting: OrdersLinkFormatting,
                BoldFormatting,
              }}
              description={{
                text: "First step to troubleshoot order sync",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={2}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="From the <BoldFormatting>Settings</BoldFormatting> page, click <LinkFormatting>Integrations</LinkFormatting> to go to the <BoldFormatting>Faire: Sell Wholesale</BoldFormatting> sales channel in <BoldFormatting>Shopify</BoldFormatting>."
              values={{
                LinkFormatting: IntegrationsLinkFormatting,
                BoldFormatting,
              }}
              description={{
                text: "Second step to troubleshoot order sync",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={3}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Once on Shopify and in the Faire sales channel, go to <BoldFormatting>Preferences</BoldFormatting>."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Third step to troubleshoot order sync",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={4}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Scroll to the <BoldFormatting>order sync</BoldFormatting> section within the <BoldFormatting>Preferences</BoldFormatting> tab and ensure the setting is enabled. If it’s off, select the <BoldFormatting>Turn On</BoldFormatting> button."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Fourth step to troubleshoot order sync",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={5}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Check that your products are linked correctly by going to the <BoldFormatting>Product Linking</BoldFormatting> tab on the left side of the Shopify dashboard under the <BoldFormatting>Faire: Sell Wholesale</BoldFormatting> sales channel. If there are any linking issues, a banner at the top of the page will indicate how many products are affected."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Fifth step to troubleshoot order sync",
              }}
            />
          </Typography>
        </LiItem>
      </ol>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="If these steps don’t resolve your order sync issue, contact our support team for more help."
          description={{
            text: "Conclusion for troubleshooting order sync",
          }}
        />
      </Typography>
    </Column>
  );
};

const IntegrationsLinkFormatting = (chunks: JSX.Element) => (
  <Button variant="tertiary" linkTo={brandPortalIntegrations()} target="_blank">
    <Bold>{chunks}</Bold>
  </Button>
);

const OrdersLinkFormatting = (chunks: JSX.Element) => (
  <Button variant="tertiary" linkTo={brandPortalOrders()} target="_blank">
    <Bold>{chunks}</Bold>
  </Button>
);

const BoldFormatting = (chunks: JSX.Element) => <Bold>{chunks}</Bold>;
